import React, { FunctionComponent, useEffect, useState } from "react";
import { BFLoader } from '@components/common/loader/main';
import { getGelatoCatalog, getGelatoCatalogs, getGelatoProducts, GelatoProductSearchFilters, GelatoProducts } from "@api/gelato";
import { formatCamelOrPascalCaseToSpaces } from "./helpers/formatting";
import './styles/GelatoPrintConfig.scss';
import { ListDropdown, ListOption } from "../library/dropdown";

interface ListOptionWithTitle {
  options: ListOption[];
  title: string;
}

const DEFAULT_PRODUCT_ATTRIBUTE_FILTERS = {
  attributeFilters: {
    CoatingType: [],
    ColorType: [],
    Orientation: [],
    PaperFormat: [],
    PaperType: [],
    ProductStatus: [],
    ProtectionType: [],
    ShapeEdgeType: [],
    SpotFinishingType: [],
    Variable: []
  },
  limit: 5,
  offset: 0
};

interface GelatoPrintConfigProps {
  handleGelatoProductUid: (productUid: string) => void;
}

/**
 * Only PDF (check attachments)
 * Must be public
 */
export const GelatoPrintConfig: FunctionComponent<GelatoPrintConfigProps> = ({ handleGelatoProductUid }) => {
  const [catalog, setCatalog] = useState<ListOption | undefined>(undefined);
  const [catalogs, setCatalogs] = useState<ListOption[]>([]);
  const [catalogsLoading, setCatalogsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [productAttributeFilters, setProductAttributeFilters] = useState<GelatoProductSearchFilters>(DEFAULT_PRODUCT_ATTRIBUTE_FILTERS);
  const [productAttributes, setProductAttributes] = useState<ListOptionWithTitle[]>([]);
  const [productAttributesLoading, setProductAttributesLoading] = useState(false);
  const [productSearch, setProductSearch] = useState<GelatoProducts | undefined>(undefined);
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [selectedProductUid, setSelectedProductUid] = useState('');

  useEffect(() => {
    const getCatalogs = async (): Promise<void> => {
      setCatalogsLoading(true);
      try {
        const response = await getGelatoCatalogs();

        const catalogOptions = response.data.map((c) => {
          const option: ListOption = { label: c.title, value: c.catalogUid };
          return option;
        });

        setCatalogs(catalogOptions);
      } catch (err) {
        setError(err);
      } finally {
        setCatalogsLoading(false);
      }
    };
    getCatalogs();
  }, []);

  useEffect(() => {
    const getCatalogProductAttributes = async (): Promise<void> => {
      if (catalog) {
        setProductAttributesLoading(true);
        try {
          const response = await getGelatoCatalog(catalog.value as string);

          const productOptions = response.productAttributes.map((product) => {
            const options: ListOption[] = product.values.map((value) => ({ value: value.productAttributeValueUid, label: value.title }));
            const option: ListOptionWithTitle = { options, title: product.productAttributeUid };
            return option;
          });

          setProductAttributes(productOptions);
        } catch (err) {
          setError(err);
        } finally {
          setProductAttributesLoading(false);
        }
      } else {
        setProductAttributes([]);
        setProductAttributeFilters(DEFAULT_PRODUCT_ATTRIBUTE_FILTERS);
      }
    };
    getCatalogProductAttributes();
  }, [catalog]);

  useEffect(() => {
    const getProductSearchResults = async (): Promise<void> => {
      if (catalog) {
        setProductSearchLoading(true);
        try {
          const response = await getGelatoProducts(catalog.value as string, productAttributeFilters);
          setProductSearch(response);
        } catch (err) {
          setError(err);
        } finally {
          setProductSearchLoading(false);
        }
      } else {
        setProductSearch(undefined);
      }
    };
    getProductSearchResults();
  }, [catalog, productAttributeFilters]);

  const step1 = (
    <>
      <h3 className="gelato-print-config__step-titles">{bfTranslate('Step 1: Select a catalog')}</h3>
      <div className="gelato-listdropdown">
        <label className="bf-label bf-label--primary" htmlFor="catalogs">{bfTranslate('Catalog')}</label>
        <ListDropdown
          className="gelato-listdropdown__dropdown"
          onChange={(option): void => {
            setCatalog(option);
          }}
          openOnClick
          openOnHover={false}
          options={catalogs}
          placeholder={bfTranslate('Select a catalog...')}
          searchable
          value={catalog}
        />
      </div>
    </>
  );

  const step2 = (
    <>
      <h3 className={`gelato-print-config__step-titles ${!catalog ? 'disabled' : ''}`}>{bfTranslate('Step 2: Select your product attributes')}</h3>
      {productAttributesLoading && <BFLoader />}
      {catalog && !productAttributesLoading && (
        <div className="gelato-listdropdowns">
          {productAttributes.map((product) => {
            const { options, title } = product;
            // NOTE: a gelato catalog product attribute may have only one option with a value of "none", "activated", or "no"
            // we don't need to search on "none", "activated", or "no" so we hide those selects
            if (options.length === 0 || (options.length === 1 && ['none', 'activated', 'no'].includes(options[0].value as string))) {
              return null;
            }
            const filter = productAttributeFilters.attributeFilters[title] as string[];
            const value = filter && filter.length > 0 ? options.find((option) => option.value === filter[0]) : undefined;
            return (
              <React.Fragment key={title}>
                <div className="gelato-listdropdown">
                  <label className="bf-label bf-label--primary" htmlFor={title}>{formatCamelOrPascalCaseToSpaces(title)}</label>
                  <ListDropdown
                    className="gelato-listdropdown__dropdown"
                    onChange={(option: ListOption | null): void => {
                      setProductAttributeFilters({
                        ...productAttributeFilters,
                        attributeFilters: {
                          ...productAttributeFilters.attributeFilters,
                          [title]: option ? [option.value] : []
                        }
                      });
                    }}
                    openOnClick
                    openOnHover={false}
                    options={options}
                    placeholder={`${bfTranslate("Select")} ${bfTranslate(formatCamelOrPascalCaseToSpaces(title).toLowerCase())}`}
                    searchable
                    value={value}
                  />
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </>
  );

  const step3 = (
    <>
      <h3 className={`gelato-print-config__step-titles ${!catalog ? 'disabled' : ''}`}>{bfTranslate('Step 3: Select a product')}</h3>
      {(productAttributesLoading || productSearchLoading) && <BFLoader />}
      {!productAttributesLoading && !productSearchLoading && productSearch && (
        <>
          {productSearch.products.length > 0 && (
            <ul>
              {productSearch.products.map((product) => {
                const { attributes, productUid } = product;
                return (
                  <li className="gelato-product" key={productUid}>
                    <button
                      aria-pressed={selectedProductUid === productUid}
                      className="gelato-product__button"
                      onClick={(): void => {
                        handleGelatoProductUid(productUid);
                        setSelectedProductUid(productUid);
                      }}
                      type="button"
                    >
                      {Object.entries(attributes).map((attribute) => <span className="attribute" key={attribute[0]}><span className="attribute__label">{formatCamelOrPascalCaseToSpaces(attribute[0])}:</span> <span className="attribute__value">{attribute[1]}</span></span>)}
                      <span className="attribute"><span className="attribute__label">Product UID:</span> <span className="attribute__value">{productUid}</span></span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {productSearch.products.length === 0 && <p className="gelato-print-config__error-state"><span className="bff-warning" />{bfTranslate('No products found. Please change your product attributes and try again.')}</p>}
        </>
      )}
    </>
  );

  return (
    <div className="gelato-print-config">
      {catalogsLoading ? <BFLoader /> : (
        <>
          {error && <p className="gelato-print-config__error-state"><span className="bff-warning" />{error.message}</p>}
          {!error && (
            <div className="gelato-print-config__wrapper">
              {step1}
              {step2}
              {step3}
            </div>
          )}
        </>
      )}
    </div>
  );
};
